import { keepPreviousData } from '@tanstack/react-query';
import { useQueryWithErrorLog } from '#app/hooks/queries/useQueryWithErrorLog';
import { MockableApi as Api } from '#app/api/mockableApi';
import { QUERY_KEYS } from '#app/constants/queryKeys';
import { QUERY_CACHE_TIMES } from '#app/constants/queryCacheTimes';
import type { ApiResponse } from '#app/types/api';

/**
 * Тип данных робоэдвайзера из списка
 */
export interface RoboadvisorItem {
  id: number;
  uuid: string;
  user_id: number;
  account_id: string;
  client_type: string;
  strategy?: string; // ID стратегии, выбранной при создании робо-эдвайзера
}

/**
 * Функция для загрузки списка подключенных робоэдвайзеров
 */
const fetchRoboadvisorConnection = async ({
  clientId,
}: {
  clientId: number | string | undefined;
}): Promise<RoboadvisorItem[]> => {
  return new Promise((resolve, reject) => {
    Api.get('robo-advisor-client', 'index', { user_id: clientId }).all(
      (response: ApiResponse<RoboadvisorItem[]>) => {
        resolve(response.data || []);
      },
      (error: any) => {
        reject(error);
      }
    );
  });
};

/**
 * Функция для загрузки робоэдвайзера по ID
 */
export const fetchRoboadvisorById = async ({
  clientId,
  roboId,
}: {
  clientId: number | string;
  roboId: number | string;
}): Promise<RoboadvisorItem | null> => {
  return new Promise((resolve, reject) => {
    Api.get('robo-advisor-client', 'index', { user_id: clientId })
      .where('id', roboId)
      .first(
        (response: ApiResponse<RoboadvisorItem | null>) => {
          resolve(response.data || null);
        },
        (error: any) => {
          reject(error);
        }
      );
  });
};

interface UseRoboadvisorConnectionQueryReturn {
  roboData: RoboadvisorItem[];
  data: RoboadvisorItem[] | undefined;
  isLoading: boolean;
  isPending: boolean;
  error: any;
  refetch: () => void;
}

/**
 * Hook для проверки подключения робоэдвайзера
 */
export const useRoboadvisorConnectionQuery = (
  clientId: number | string | undefined
): UseRoboadvisorConnectionQueryReturn => {
  const query = useQueryWithErrorLog(
    {
      queryKey: QUERY_KEYS.roboadvisor.all(clientId),
      queryFn: () => fetchRoboadvisorConnection({ clientId }),
      enabled: !!clientId,
      staleTime: QUERY_CACHE_TIMES.FAST_UPDATE,
      placeholderData: keepPreviousData,
    },
    'useRoboadvisorConnectionQuery'
  );

  return {
    ...query,
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    error: query.error,
    refetch: query.refetch,
    roboData: query.data || [],
  };
};

/**
 * Hook для получения робоэдвайзера по ID
 */
export const useRoboadvisorByIdQuery = (
  clientId: number | string | undefined,
  roboId: number | string | undefined
) => {
  return useQueryWithErrorLog(
    {
      queryKey: QUERY_KEYS.roboadvisor.detail(roboId, clientId),
      queryFn: () => fetchRoboadvisorById({ clientId: clientId!, roboId: roboId! }),
      enabled: !!(clientId && roboId),
      staleTime: QUERY_CACHE_TIMES.FAST_UPDATE,
      placeholderData: keepPreviousData,
    },
    'useRoboadvisorByIdQuery'
  );
};
